import React from 'react';

const TAKEOFF_PILL: Record<string, { bg: string; border: string; color: string }> = {
  complete:    { bg: 'rgba(34,197,94,0.14)',   border: 'rgba(34,197,94,0.50)',   color: '#4ade80' },
  wall_left:   { bg: 'rgba(34,197,94,0.14)',   border: 'rgba(34,197,94,0.50)',   color: '#4ade80' },
  wall_right:  { bg: 'rgba(34,197,94,0.14)',   border: 'rgba(34,197,94,0.50)',   color: '#4ade80' },
  remaining:   { bg: 'rgba(168,85,247,0.18)',  border: 'rgba(168,85,247,0.55)',  color: '#c084fc' },
  wall_middle: { bg: 'rgba(168,85,247,0.18)',  border: 'rgba(168,85,247,0.55)',  color: '#c084fc' },
  opening:     { bg: 'rgba(249,115,22,0.14)',  border: 'rgba(249,115,22,0.50)',  color: '#fb923c' },
  elec:        { bg: 'rgba(6,182,212,0.13)',   border: 'rgba(6,182,212,0.48)',   color: '#22d3ee' },
  kitchen:    { bg: 'rgba(253,186,89,0.15)',  border: 'rgba(253,186,89,0.50)',  color: '#0CB4E4' },
  staircase:  { bg: 'rgba(96,165,250,0.15)',  border: 'rgba(96,165,250,0.50)',  color: '#60a5fa' },
  fireplace:  { bg: 'rgba(249,115,22,0.15)',  border: 'rgba(249,115,22,0.50)',  color: '#f97316' },
  cabinet:    { bg: 'rgba(52,211,153,0.15)',  border: 'rgba(52,211,153,0.50)',  color: '#34d399' },
};

export function renderInline(text: string, onPillClick?: (tag: string, text: string) => void): React.ReactNode[] {
  // Split on **bold**, [tag:text] color pills, and [label](url) links
  const parts = text.split(/(\*\*[^*]+\*\*|\[[a-z]+:[^\]]+\]|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={i}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0CB4E4', textDecoration: 'underline', textUnderlineOffset: 2 }}
        >
          {linkMatch[1]}
        </a>
      );
    }
    const pillMatch = part.match(/^\[([a-z]+):([^\]]+)\]$/);
    if (pillMatch) {
      const style = TAKEOFF_PILL[pillMatch[1]];
      if (style) {
        const clickable = !!onPillClick;
        return (
          <span
            key={i}
            role={clickable ? 'button' : undefined}
            onClick={clickable ? () => onPillClick(pillMatch[1], pillMatch[2]) : undefined}
            style={{
              display: 'inline-block',
              background: style.bg,
              border: `1px solid ${style.border}`,
              color: style.color,
              borderRadius: 4,
              padding: '0px 6px',
              fontSize: '0.92em',
              fontWeight: 600,
              lineHeight: 1.6,
              verticalAlign: 'middle',
              cursor: clickable ? 'pointer' : undefined,
              userSelect: clickable ? 'none' : undefined,
            }}
          >
            {pillMatch[2]}
          </span>
        );
      }
    }
    return part;
  });
}

export function AgentMarkdown({ text, onPillClick }: { text: string; onPillClick?: (tag: string, text: string) => void }) {
  if (!text) return null;
  const elements: React.ReactNode[] = [];
  const listItems: React.ReactNode[] = [];
  let listType: 'ul' | 'ol' | null = null;
  const tableRows: string[][] = [];

  const flushList = (key: number) => {
    if (!listItems.length) return;
    const Tag = listType === 'ol' ? 'ol' : 'ul';
    elements.push(
      <Tag key={key} className={`my-1 space-y-0.5 pl-4 ${listType === 'ol' ? 'list-decimal' : 'list-disc'}`}>
        {[...listItems]}
      </Tag>,
    );
    listItems.length = 0;
    listType = null;
  };

  const flushTable = (key: number) => {
    if (!tableRows.length) return;
    const [header, , ...body] = tableRows;
    elements.push(
      <div key={key} className="my-2 overflow-x-auto">
        <table className="w-full border-collapse text-meta">
          <thead>
            <tr>
              {(header ?? []).map((cell, ci) => (
                <th key={ci} className="whitespace-nowrap border-b border-ink-950/25 px-2 py-1 text-left font-semibold text-ink-950">
                  {cell.trim()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? 'bg-ink-950/[0.06]' : ''}>
                {row.map((cell, ci) => (
                  <td key={ci} className="whitespace-nowrap border-b border-ink-950/10 px-2 py-1 text-ink-950/80">
                    {cell.trim()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
    tableRows.length = 0;
  };

  text.split('\n').forEach((line, i) => {
    if (line.startsWith('|')) {
      flushList(i);
      const cells = line.split('|').slice(1, -1);
      tableRows.push(cells);
    } else if (/^#{1,3} /.test(line)) {
      flushTable(i);
      flushList(i);
      const level = (line.match(/^(#+)/)![1]).length;
      elements.push(
        <p key={i} className={`${level === 1 ? 'font-bold' : 'font-semibold'} mt-2 mb-0.5`}>
          {renderInline(line.replace(/^#{1,3} /, ''), onPillClick)}
        </p>,
      );
    } else if (/^[-*] /.test(line)) {
      flushTable(i);
      if (listType !== 'ul') { flushList(i); listType = 'ul'; }
      listItems.push(<li key={i}>{renderInline(line.slice(2), onPillClick)}</li>);
    } else if (/^\d+\. /.test(line)) {
      flushTable(i);
      if (listType !== 'ol') { flushList(i); listType = 'ol'; }
      listItems.push(<li key={i}>{renderInline(line.replace(/^\d+\. /, ''), onPillClick)}</li>);
    } else if (line.trim() === '') {
      flushTable(i);
      flushList(i);
    } else {
      flushTable(i);
      flushList(i);
      elements.push(<p key={i} className="my-0.5">{renderInline(line, onPillClick)}</p>);
    }
  });
  flushTable(-1);
  flushList(-1);

  return <>{elements}</>;
}
